import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface Project {
  id: string;
  name: string;
  ticketIntegrationId?: string | null;
}

interface Tracker {
  id: string;
  provider: string;
  label: string;
  projectName: string;
  target: string;
}

/**
 * Tickets → Configuration: which tracker profile each project's tickets go to.
 *
 * Trackers are org property in practice — a team's Jira is a team's Jira,
 * whichever project the session came from — so any project may route to any
 * issue-capable profile in the org. "Ask each time" is the default and keeps
 * the picker in the create dialog.
 */
export function TicketRoutingDialog({
  open,
  onClose,
  orgId,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
}) {
  const all = useT();
  const c = all.integrations.routing;
  const common = all.common;
  const qc = useQueryClient();
  const [saved, setSaved] = useState<string | null>(null);

  const projects = useQuery({
    enabled: open && !!orgId,
    queryKey: ['projects', orgId],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/projects`)).data.data as Project[],
  });

  const trackers = useQuery({
    enabled: open && !!orgId && !!projects.data?.length,
    queryKey: ['org-trackers', orgId],
    queryFn: async () =>
      (
        await api.get(
          `/v1/orgs/${orgId}/projects/${projects.data![0]!.id}/integrations/trackers`,
        )
      ).data.data as Tracker[],
  });

  const route = useMutation({
    mutationFn: async (v: { projectId: string; integrationId: string | null }) =>
      api.patch(`/v1/orgs/${orgId}/projects/${v.projectId}`, {
        ticketIntegrationId: v.integrationId,
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['projects', orgId] });
      setSaved(v.projectId);
      setTimeout(() => setSaved(null), 1500);
    },
  });

  const trackerName = (t: Tracker) =>
    `${t.provider}${t.label !== 'default' ? ` (${t.label})` : ''}${t.target ? `, ${t.target}` : ''}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <AltRouteIcon color="primary" />
          <span>{c.title}</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Where each project's tickets are filed by default. A project can route to any tracker
          profile in the organization; "Ask each time" leaves the choice in the create dialog.
        </Typography>

        {(projects.isPending || trackers.isPending) && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
          </Stack>
        )}

        {trackers.data && trackers.data.length === 0 && (
          <Alert severity="info">
            No issue tracker is connected yet. Configure GitHub, Jira or another tracker in the
            Integrations Hub first.
          </Alert>
        )}

        {projects.data && trackers.data && trackers.data.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{c.project}</TableCell>
                <TableCell>{c.goesTo}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.data.map((p) => (
                <TableRow key={p.id}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {p.name}
                    {saved === p.id && (
                      <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                        saved ✓
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={p.ticketIntegrationId ?? ''}
                      onChange={(e) =>
                        route.mutate({ projectId: p.id, integrationId: (e.target.value as string) || null })
                      }
                    >
                      <MenuItem value="">
                        <em>{c.askEachTime}</em>
                      </MenuItem>
                      {trackers.data.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {trackerName(t)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {route.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(route.error as { response?: { data?: { error?: { message?: string } } } })?.response
              ?.data?.error?.message ?? c.saveFailed}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{common.close}</Button>
      </DialogActions>
    </Dialog>
  );
}
